import apiService from "../api/api.service";
import {
  Company,
  CreateCompanyDto,
  UpdateCompanyDto,
  CompanySettings,
  AnnualLeaveLimitResponse,
  AboutUs,
  PaymentRecord,
  UpdateSubscriptionDto,
  CompanySetupStatus
} from "../../types/company.types";
// API rotaları

class CompanyService {
    private static instance: CompanyService;

    public static getInstance(): CompanyService {
        if (!CompanyService.instance) {
            CompanyService.instance = new CompanyService();
        }
        return CompanyService.instance;
    }

    // Private constructor
    private constructor() {}

    public async getCompanySettings(companyId: number): Promise<CompanySettings> {
        const response = await apiService.get<CompanySettings>(`/companies/${companyId}/settings`);
        return response;
    }

    public async updateCompanySettings(companyId: number, settings: CompanySettings): Promise<CompanySettings> {
        const response = await apiService.put<CompanySettings>(`/companies/${companyId}/settings`, settings);
        return response;
    }

    public async getAnnualLimit(companyId: number): Promise<AnnualLeaveLimitResponse> {
        const response = await apiService.get<AnnualLeaveLimitResponse>(`/companies/annual-leave-limit/${companyId}`);
        return response;
    }

    /**
     * Tüm şirketleri getirir
     * @returns Şirket listesi
     */
    public async getAllCompanies(): Promise<Company[]> {
        try {
            const response = await apiService.get<Company[]>(`/companies`);
            return response;
        } catch (error) {
            throw error;
        }
    }

    public async getCompanyActiveUsers(companyId:number):Promise<any>{
        const response = await apiService.get<any>(`/companies/active-user/${companyId}`);
        return response;
    }

    public async getCompanySetupStatus(companyId: number): Promise<CompanySetupStatus> {
        return apiService.get<CompanySetupStatus>(`/companies/setup-status/${companyId}`);
      }
      
    /**
     * Belirli bir şirketi ID'ye göre getirir
     * @param id Şirket ID
     * @returns Şirket nesnesi
     */
    public async getCompanyById(id: number): Promise<Company> {
        try {
            const response = await apiService.get<Company>(`/companies/${id}`);
            return response;
        } catch (error) {
            throw error;
        }
    }

    /**
     * Yeni bir şirket oluşturur
     * @param companyData Şirket bilgileri
     * @returns Oluşturulan şirket nesnesi
     */
    public async createCompany(companyData: CreateCompanyDto, otp: string | null, sessionToken: string | null): Promise<Company> {
        try {
            const data = {
                ...companyData,
                otp: otp || null,
                sessionToken: sessionToken || null,
            }
            const response = await apiService.post<Company>(`/companies`, data);
            return response;
        } catch (error) {
            throw error;
        }
    }

    /**
     * Mevcut bir şirketi günceller
     * @param id Şirket ID
     * @param companyData Güncellenecek şirket bilgileri
     * @returns Güncellenen şirket nesnesi
     */
    public async updateCompany(id: number, companyData: UpdateCompanyDto): Promise<Company> {
        try {
            const response = await apiService.put<Company>(`/companies/${id}`, companyData);
            return response;
        } catch (error) {
            throw error;
        }
    }

    /**
     * Bir şirketi siler
     * @param id Silinecek şirket ID
     * @returns İşlem sonucu
     */
    public async deleteCompany(id: number): Promise<any> {
        try {
            const response = await apiService.delete<any>(`/companies/${id}`);
            return response;
        } catch (error) {
            throw error;
        }
    }

    /**
     * Şirketin durumunu değiştirir (aktif/pasif/askıya alınmış)
     * @param id Şirket ID
     * @param status Yeni durum değeri
     * @returns Güncellenen şirket nesnesi
     */
    public async changeCompanyStatus(id: number, status: string): Promise<Company> {
        try {
            const response = await apiService.patch<Company>(`/companies/${id}/status`, { status });
            return response;
        } catch (error) {
            throw error;
        }
    }

    public async getAboutUs(): Promise<AboutUs> {
        const response = await apiService.get<AboutUs>(`/settings/about`);
        return response;
    }

    public async updateAboutUs(aboutUs: AboutUs): Promise<AboutUs> {
        const response = await apiService.put<AboutUs>(`/settings/about`, aboutUs);
        return response;
    }

    /**
     * Şirketin kullanıcı limitini günceller
     * @param companyId Şirket ID
     * @param employeeCount Yeni kullanıcı sayısı
     * @returns Güncellenen ayarlar
     */
    public async updateEmployeeCount(companyId: number, employeeCount: number): Promise<CompanySettings> {
        try {
            const response = await apiService.patch<CompanySettings>(`/companies/${companyId}/employee-count`, { employeeCount });
            return response;
        } catch (error) {
            throw error;
        }
    }

    public async sendOtp(phone: string): Promise<any> {
        const response = await apiService.post<any>(`/companies/send-otp`, { phone });
        return response;
    }

    async getCompanyDetailsForSuperAdmin(id: number): Promise<any> {
        const response = await apiService.get(`super-admin/company/${id}`);
        return response;
    }

    public async getCompanyPaymentHistory(id: number): Promise<PaymentRecord[]> {
        const response = await apiService.get<PaymentRecord[]>(`super-admin/company/${id}/payment-history`);
        return response;
    }

    public async updateSubscriptionBySuperAdmin(id: number, data: UpdateSubscriptionDto): Promise<Company> {
        return apiService.patch<Company>(`/super-admin/company/${id}/subscription`, data);
    }

  // Süper admin ödeme raporları (gerçek API)
  public async getPaymentReports(params?: {
    companyId?: number;
    status?: 'pending' | 'paid' | 'failed';
    planType?: string;
    q?: string;
    startDate?: string;
    endDate?: string;
    page?: number;
    pageSize?: number;
  }): Promise<any> {
    return apiService.get(`/super-admin/payment-reports`, { params });
  }

}

export default CompanyService.getInstance();